import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import {
  CreateVolumeScenario,
  CreateVolumeScenarioTarget,
  UpdateScenarioVolumes,
  UpdateVolumeScenario,
} from '@repo/backend/crt'
import { MONTH_KEYS, YEAR_FLAG_VALUES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const volumeScenariosSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export const targetMaterialsSearchSchema = z.object({
  keyword: z.string().optional(),
  // Which year the base/adjusted volume columns total; follows the target editor's CY/NY toggle.
  yearFlag: z.enum(YEAR_FLAG_VALUES).optional(),
  // Narrows the base/adjusted volume columns to a single month; omitted totals the full year.
  month: z.enum(MONTH_KEYS).optional(),
  ...datatableSearchSchema.shape,
})

export type VolumeScenariosSearch = z.infer<typeof volumeScenariosSearchSchema>
export type TargetMaterialsSearch = z.infer<typeof targetMaterialsSearchSchema>

export type VolumeScenariosList = Awaited<ReturnType<typeof getVolumeScenariosList>>
export type VolumeScenario = VolumeScenariosList['results'][number]
export type VolumeScenarioWithTargets = Awaited<ReturnType<typeof getVolumeScenarioById>>
export type VolumeScenarioTarget = VolumeScenarioWithTargets['targets'][number]
export type VolumeScenarioTargetVolume = VolumeScenarioTarget['volumes'][number]
export type TargetMaterial = Awaited<ReturnType<typeof getTargetMaterials>>['results'][number]

type UseCreateVolumeScenarioArgs = {
  queries: VolumeScenariosSearch
  handleOnSuccess: (volumeScenarioId: number) => void
}

type UseUpdateVolumeScenarioArgs = QueryHandlers & {
  volumeScenarioId: number
  // When false the hook stays silent so a caller coordinating several mutations can show one toast.
  showToast?: boolean
}

type UseDeleteVolumeScenariosArgs = QueryHandlers & {
  volumeScenarioIds: number[]
  queries: VolumeScenariosSearch
}

type UseCreateTargetArgs = QueryHandlers & {
  volumeScenarioId: number
}

type UseDeleteTargetArgs = QueryHandlers & {
  volumeScenarioId: number
}

type UseUpdateTargetVolumesArgs = QueryHandlers & {
  volumeScenarioId: number
  // When false the hook stays silent so a caller coordinating several mutations can show one toast.
  showToast?: boolean
}

export type TargetVolumesUpdate = UpdateScenarioVolumes['updates'][number]

async function getVolumeScenariosList(search: VolumeScenariosSearch) {
  const api = getApi()
  const res = await api.crt['volume-scenarios'].$get({
    query: stringifyValues(search),
  })

  return await res.json()
}

async function getVolumeScenarioById(id: string) {
  const api = getApi()
  const res = await api.crt['volume-scenarios'][':id'].$get({
    param: { id },
  })

  return await res.json()
}

async function createVolumeScenario(data: CreateVolumeScenario) {
  const api = getApi()

  const res = await api.crt['volume-scenarios'].$post({
    json: data,
  })

  return await res.json()
}

async function updateVolumeScenario(volumeScenarioId: number, details: UpdateVolumeScenario) {
  const api = getApi()

  await api.crt['volume-scenarios'][':id'].$patch({
    param: { id: String(volumeScenarioId) },
    json: details,
  })
}

async function deleteVolumeScenarios(volumeScenarioIds: number[]) {
  const api = getApi()

  await api.crt['volume-scenarios'].bulk.$delete({
    json: { ids: volumeScenarioIds },
  })
}

async function createTarget(volumeScenarioId: number, data: CreateVolumeScenarioTarget) {
  const api = getApi()

  const res = await api.crt['volume-scenarios'][':id'].targets.$post({
    param: { id: String(volumeScenarioId) },
    json: data,
  })

  return await res.json()
}

async function deleteTarget(volumeScenarioId: number, targetId: number) {
  const api = getApi()

  await api.crt['volume-scenarios'][':id'].targets[':targetId'].$delete({
    param: { id: String(volumeScenarioId), targetId: String(targetId) },
  })
}

async function updateScenarioVolumes(
  volumeScenarioId: number,
  updates: UpdateScenarioVolumes['updates'],
) {
  const api = getApi()

  await api.crt['volume-scenarios'][':id'].volumes.$patch({
    param: { id: String(volumeScenarioId) },
    json: { updates },
  })
}

async function getTargetMaterials(
  volumeScenarioId: string,
  targetId: string,
  queries: TargetMaterialsSearch = {},
) {
  const api = getApi()
  const res = await api.crt['volume-scenarios'][':id'].targets[':targetId'].materials.$get({
    param: { id: volumeScenarioId, targetId },
    query: stringifyValues(queries),
  })

  return await res.json()
}

async function getAvailableBasePeriods() {
  const api = getApi()
  const res = await api.crt['volume-scenarios']['base-periods'].$get()

  const { periods } = await res.json()
  return periods.map(parseDateOnly)
}

export function useVolumeScenariosList(
  queries: VolumeScenariosSearch,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['volumeScenarios', queries],
    queryFn: () => getVolumeScenariosList(queries),
    enabled: options?.enabled ?? true,
  })
}

export const volumeScenarioByIdQueryOptions = (id: string) => ({
  queryKey: ['volumeScenarioById', id],
  queryFn: () => getVolumeScenarioById(id),
})

export function useVolumeScenarioById(id: string) {
  return useQuery(volumeScenarioByIdQueryOptions(id))
}

export const targetMaterialsQueryOptions = (
  volumeScenarioId: string,
  targetId: string,
  queries: TargetMaterialsSearch = {},
) => ({
  queryKey: ['volumeScenarioTargetMaterials', volumeScenarioId, targetId, queries],
  queryFn: () => getTargetMaterials(volumeScenarioId, targetId, queries),
})

export function useTargetMaterials(
  volumeScenarioId: string,
  targetId: string,
  queries: TargetMaterialsSearch = {},
) {
  return useQuery(targetMaterialsQueryOptions(volumeScenarioId, targetId, queries))
}

export function useAvailableBasePeriods() {
  return useQuery({
    queryKey: ['volumeScenarioBasePeriods'],
    queryFn: getAvailableBasePeriods,
  })
}

export function useCreateVolumeScenario({ queries, handleOnSuccess }: UseCreateVolumeScenarioArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['volumeScenarios', queries],
    mutationFn: createVolumeScenario,
    onSuccess: (volumeScenario) => {
      handleOnSuccess(volumeScenario.id)

      queryClient.invalidateQueries({
        queryKey: ['volumeScenarios', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('volumeScenarios.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.volumeScenarios.createFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateVolumeScenario({
  volumeScenarioId,
  handleOnSuccess,
  showToast = true,
}: UseUpdateVolumeScenarioArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateVolumeScenario', volumeScenarioId],
    mutationFn: (details: UpdateVolumeScenario) => updateVolumeScenario(volumeScenarioId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['volumeScenarioById', String(volumeScenarioId)],
      })

      if (!showToast) return

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('volumeScenarios.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      if (!showToast) return

      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.volumeScenarios.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteVolumeScenarios({
  volumeScenarioIds,
  queries,
  handleOnSuccess,
}: UseDeleteVolumeScenariosArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteVolumeScenarios'],
    mutationFn: () => deleteVolumeScenarios(volumeScenarioIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['volumeScenarios', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('volumeScenarios.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.volumeScenarios.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useCreateTarget({ volumeScenarioId, handleOnSuccess }: UseCreateTargetArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createVolumeScenarioTarget', volumeScenarioId],
    mutationFn: (data: CreateVolumeScenarioTarget) => createTarget(volumeScenarioId, data),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['volumeScenarioById', String(volumeScenarioId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('volumeScenarios.createTargetSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.volumeScenarios.createTargetFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteTarget({ volumeScenarioId, handleOnSuccess }: UseDeleteTargetArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteVolumeScenarioTarget', volumeScenarioId],
    mutationFn: (targetId: number) => deleteTarget(volumeScenarioId, targetId),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['volumeScenarioById', String(volumeScenarioId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('volumeScenarios.deleteTargetSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.volumeScenarios.deleteTargetFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateTargetVolumes({
  volumeScenarioId,
  handleOnSuccess,
  showToast = true,
}: UseUpdateTargetVolumesArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateVolumeScenarioTargetVolumes', volumeScenarioId],
    mutationFn: (updates: TargetVolumesUpdate[]) =>
      updateScenarioVolumes(volumeScenarioId, updates),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['volumeScenarioById', String(volumeScenarioId)],
      })

      if (!showToast) return

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('volumeScenarios.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      if (!showToast) return

      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.volumeScenarios.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
